import { useCallback, useEffect, useState } from 'react';
/**
 * Tracks whether a native scroll viewport has more content above or below it.
 *
 * @param ref - The scroll viewport to observe and read scroll positions from.
 * @param contentRef - Optional inner content to observe for size changes. In a
 * capped viewport, streaming content or wrapping can change scrollHeight without
 * resizing the viewport itself. Observing the content keeps overflow state fresh
 * without waiting for a scroll event. Omit for viewport-only size observation.
 */
export function useScrollOverflow(ref, contentRef) {
    const [canScrollUp, setCanScrollUp] = useState(false);
    const [canScrollDown, setCanScrollDown] = useState(false);
    const check = useCallback(() => {
        const el = ref.current;
        if (!el)
            return;
        setCanScrollUp(el.scrollTop > 0);
        setCanScrollDown(el.scrollTop + el.clientHeight < el.scrollHeight - 1);
    }, [ref]);
    useEffect(() => {
        const el = ref.current;
        if (!el)
            return;
        check();
        el.addEventListener('scroll', check, { passive: true });
        const observer = new ResizeObserver(check);
        observer.observe(el);
        if (contentRef?.current)
            observer.observe(contentRef.current);
        return () => {
            el.removeEventListener('scroll', check);
            observer.disconnect();
        };
    }, [ref, contentRef, check]);
    return { canScrollUp, canScrollDown };
}
//# sourceMappingURL=useScrollOverflow.js.map